import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useHistory, Link } from 'react-router-dom';
import {
  Button,
  Card,
  Checkbox,
  Grid,
  Icon,
  Typography,
  TextField,
  IconButton,
  FormControl,
  FormHelperText
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import { BackdropCircular, Breadcrumb, InfoTooltip } from 'components';
import logo from 'components/Logo/logo-dermpro.svg';
import {
  getMasterCatalogProductsApi,
  addTasksToQueueApi,
  getAllTasksFromQueueApi,
  getStoreProductsApi,
  getBrands,
  getStoreTaxClassesApi
} from 'api';
import { imgBaseUrl } from 'configs';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';


const useStyles = makeStyles(() => ({
  disabled: {
    filter: 'opacity(0.5) grayscale(0.9)',
  }
}));

export default function BrowseProducts() {
  const classes = useStyles();
  const params = useParams<{id: string, type: string, storeView: string}>();
  const dispatch = useDispatch();
  const history = useHistory();
  const brandProductData = useSelector((state: IReduxState) => state.brands.browseBrandsProducts);
  const brandTreatmentsData = useSelector((state: IReduxState) => state.brands.browseBrandsTreatments);
  const brandsGiftsData = useSelector((state: IReduxState) => state.brands.browseBrandsGifts);
  const allStoreProducts = useSelector((state: IReduxState) => state.products.store_products.catalogProducts);
  const {loadingFullfilmentRequest} = useSelector((state: IReduxState) => state.partners);
  const userStore = useSelector((state: IReduxState) => state.user?.profile?.stores?.[0]);
  const storeViewCode = params.storeView === 'quote' ? userStore.quoteProStoreCode : userStore.storeCode;

  const galleryData = params.type == 'treat' ? brandTreatmentsData : [...brandProductData, ...brandsGiftsData];
  const brand = galleryData.find((i) => i.option_id === params.id);
  const {
    queueTasksSkus,
    store_products,
    brand_products = [],
    productsLoading,
    loadingBrandProducts,
    storeTaxClasses = []
  } = useSelector((state: IReduxState) => state.products);
  const { quoteProducts } = useSelector((state: IReduxState) => state.quote);
  const quoteProductsSku = quoteProducts.map((p) => p.sku);
  const { store_products_sku: storeProductsSku } = store_products;
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [searchValue, setSearchValue] = useState('');
  const loading = productsLoading || loadingBrandProducts;

  const allQueueTasksSkus = [
    ...queueTasksSkus?.[userStore.quoteProStoreCode] || [],
    ...queueTasksSkus?.[userStore.storeCode] || []
  ];

  useEffect(() => {
    // should make 2 api calls for 2 store views to receive all tasks
    getAllTasksFromQueueApi({dispatch, storeViewCode: userStore.quoteProStoreCode});
    getAllTasksFromQueueApi({dispatch, storeViewCode: userStore.storeCode});

    if (searchValue.length !== 1) {
      getMasterCatalogProductsApi({dispatch, brand: `${params.id}`, name: searchValue, brandType: params.type});
    }

    if (galleryData.length === 0) {
      getBrands({dispatch, skuLike: params.type.toUpperCase()});
    }

    if (galleryData.length === 0) {
      getBrands({dispatch, skuLike: 'GIFT'});
    }

    getStoreTaxClassesApi({dispatch});
  }, []);

  useEffect(() => {
    if (!allStoreProducts.length && userStore?.isCreated) {
      getStoreProductsApi({dispatch});
    }
  }, [userStore]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    const taxableClass = storeTaxClasses.find((c) => c.class_name.toLowerCase().includes(userStore?.storeCode));

    const tasks = selectedProducts.map((product) => {
      const productAttributes = product.custom_attributes.map((item) => {
        if (item.attribute_code === 'tax_class_id') {
          return {...item, value: product.sku.includes('PROD') && taxableClass ? String(taxableClass?.class_id) : '0'};
        }

        return item;
      });

      productAttributes.push({ attribute_code: 'catalog_brand_name', value: brand.value });
      const isProductAddedToStoreView = params.storeView === 'quote'
        ? storeProductsSku.includes(product.sku)
        : quoteProductsSku.includes(product.sku);

      const body = isProductAddedToStoreView ?
        {
          status: 2, // Magento product status: enabled = 1, disabled = 2
          extension_attributes: {
            website_ids: [0, userStore.storeId, userStore.quoteProStoreId]
          }
        }
        :
        {
          ...product,
          status: 2, // Magento product status: enabled = 1, disabled = 2
          custom_attributes: productAttributes
        };

      return {
        sku: product.sku,
        name: product.name,
        body,
        requestType: isProductAddedToStoreView
          ? 'UPDATE_PRODUCT_TO_ADD_STOREVIEW'
          : 'CREATE_PRODUCT',
        storeViewCode
      };
    });

    addTasksToQueueApi({
      dispatch,
      tasks
    });

    history.push(`/browse-brand/${params.storeView}`);
  };
  const handleChange = (e, product) => {
    const {checked} = e.target;

    if (checked) {
      setSelectedProducts([...selectedProducts, product]);
    } else {
      const filteredProducts = selectedProducts.filter((p) => p.sku !== product.sku);
      setSelectedProducts(filteredProducts);
    }
  };

  const handleSelectAll = () => {
    const availableProductsToSelect = brand_products.filter((product) => {
      return !allQueueTasksSkus.includes(product.sku) &&
             (params.storeView === 'quote'
               ? !quoteProductsSku?.includes(product.sku)
               : !storeProductsSku?.includes(product.sku)
             );
    });
    setSelectedProducts(availableProductsToSelect);
  };

  const handleUnselectAll = () => {
    setSelectedProducts([]);
  };

  const handleSearch = (value) => {
    if (value.length !== 1) {
      getMasterCatalogProductsApi({dispatch, brand: `${params.id}`, name: value, brandType: params.type});
      setSelectedProducts([]);
    }
  };

  return (
    <div className="flex-grow pb-15 h-full min-h-full-screen scroll-y">
      <div className="mb-sm-30">
        {galleryData.length !== 0 ?
          <Breadcrumb
            routeSegments={[
              {
                name: `${params.storeView == 'quote' ? 'Quote' : ''} Listings`,
                path: `${params.storeView == 'quote' ? '/quoting/listings/active' : '/store-products/list/active'}`
              },
              {
                name: `Browse ${params.storeView == 'quote' ? 'Quote ' : ''}Listings`,
                path: `/browse-brand/${params.storeView}`
              },
              { name: `${brand?.value}`}
            ]}
          />
          : <Skeleton component="h4" variant="text" className="mt-8 mx-8 max-w-400" />
        }
      </div>
      <Link to={`/browse-brand/${params.storeView}`} className="mx-10 flex items-center"><Icon className="mr-2">assignment_return</Icon>Go back to Brands</Link>
      <div className="m-10">
        <form onSubmit={handleSubmit}>
          <Grid container className="sticky bg-white py-4">
            <Grid item xs={12}>
              {loading
                ? <Skeleton component="h4" variant="text" className="m-8 max-w-400" />
                :
                <div className="flex items-center">
                  <Typography variant="h3" className="p-8">
                    Items from {brand?.value}
                    <span className="text-muted font-medium text-16 pl-2">
                      ({selectedProducts.length > 0 ?
                        <span>{selectedProducts.length} {selectedProducts.length === 1 ? 'item' : 'items'} selected from </span>
                        : ''}
                      {brand_products.length})
                    </span>
                  </Typography>
                </div>
              }
            </Grid>
            <Grid item xs={12} className="flex justify-between px-8 flex-wrap items-center">
              {loading ?
                null :
                <>
                  <FormControl className="w-full max-w-600 pb-4 pr-14 flex items-center relative">
                    <TextField
                      label="Search item by name"
                      variant="outlined"
                      fullWidth
                      value={searchValue}
                      onChange={(event) => setSearchValue(event.target.value)}
                      id="back-to-top-anchor"
                      onKeyPress={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          handleSearch(searchValue);
                        }
                      }}
                      inputProps={{ className: 'pr-8' }}
                    />
                    {searchValue.length === 1 &&
                      <FormHelperText
                        className="position-bottom"
                        style={{left: '10px'}}
                      >
                        Enter two or more symbols and press Enter or Search icon
                      </FormHelperText>
                    }
                    {!!searchValue.length &&
                    <>
                      <Icon
                        className="text-muted absolute cursor-pointer"
                        fontSize="small"
                        style={{right: '65px', top: '11px'}}
                        onClick={() => {
                          setSearchValue('');
                          handleSearch('');
                        }}
                      >
                        close
                      </Icon>
                      <IconButton
                        className="absolute"
                        title="Search"
                        onClick={() => handleSearch(searchValue)}
                        style={{right: '0', top: '-3px'}}
                      >
                        <Icon>search</Icon>
                      </IconButton>
                    </>
                    }
                  </FormControl>
                  <div className="mb-4">
                    <Button
                      type="button"
                      color="primary"
                      variant="contained"
                      className="uppercase custom-check-all custom-check-all-select mr-2"
                      onClick={handleSelectAll}
                    >
                      <Icon>done_all</Icon>
                      Select All
                    </Button>
                    <Button
                      type="button"
                      color="primary"
                      variant="contained"
                      className="uppercase custom-check-all mr-2"
                      onClick={handleUnselectAll}
                    >
                      <Icon>remove</Icon>
                      Unselect All
                    </Button>
                    <Button
                      type="submit"
                      disabled={selectedProducts.length < 1}
                      variant="contained"
                      color="primary"
                      className="uppercase"
                    >
                      <Icon className="mr-2">add</Icon>
                      {params.storeView == 'quote' ? 'Add to Quoting' : 'Add to Store'}
                    </Button>
                  </div>
                </>
              }
            </Grid>
            {params.type == 'treat' &&
              <Grid item xs={5} className="relative flex px-8">
                <Typography variant="subtitle2">
                  All treatments contain multiple images allowing you to select one that is consistent
                  with your practice brand. After publishing them, you will be able to see the image selections.
                </Typography>
                <Icon color="error" fontSize="medium" >info_outline</Icon>
              </Grid>
            }
          </Grid>
          <Card elevation={25} className="p-8">
            <Grid container spacing={3}>
              {loading &&
                <Card elevation={25} className="text-center p-8">
                  <Skeleton variant="rect" width={180} height={160} />
                  <Skeleton variant="text" className="mt-10" />
                  <Skeleton variant="text" />
                  <Skeleton variant="text" className="mt-16 ml-17" width={50} />
                </Card>
              }
              {!loading  && brand_products.length === 0 && searchValue.length > 1 &&
                <Typography variant="body1" className="p-4">No matching items found.</Typography>
              }
              {!loading && brand_products.map((product, idx) => {
                const isSelected = Boolean(selectedProducts?.find((sel) => sel.sku == product.sku));
                const isOffice = product.custom_attributes?.find((attr) => attr.attribute_code === 'isoffice')?.value === '1';
                const isDisable = allQueueTasksSkus.includes(product.sku) ||
                  (params.storeView == 'quote'
                    ? quoteProductsSku?.includes(product.sku) || isOffice
                    : storeProductsSku?.includes(product.sku)
                  );

                return (
                  <Grid item xs={12} sm={3} md={2} key={idx}>
                    <Card
                      elevation={25}
                      className={clsx('text-center relative p-8', isDisable ? classes.disabled : null)}
                    >
                      {!isDisable &&
                      <Checkbox
                        color="primary"
                        style={{position: 'absolute', right: '0', top: '0'}}
                        checked={isSelected}
                        onChange={(e) => handleChange(e, product)}
                      />
                      }
                      {params.storeView == 'quote' && isOffice &&
                        <InfoTooltip
                          classNames="text-error absolute text-24"
                          style={{position: 'absolute', right: '10px', top: '10px'}}
                          title="Available in office only"
                        />
                      }
                      <img
                        src={
                          product.custom_attributes.filter((i) => i.attribute_code === 'image').length
                            ? `${imgBaseUrl}${product.custom_attributes.filter((i) => i.attribute_code === 'image').map((i) => i.value)}`
                            : logo}
                        alt={product.name}
                        className={product.custom_attributes.filter((i) => i.attribute_code === 'image').length ? 'max-w-full max-h-140' : 'w-full max-h-140'}
                        onError={(e) => {
                          (e.target as HTMLImageElement).onerror = null;
                          (e.target as HTMLImageElement).src = logo;
                        }}
                      />
                      <Typography variant="h5" className="mt-6 font-normal" style={{minHeight: '100px'}}>{product.name}</Typography>
                      <Typography variant="h5" className="mt-2">
                        {product.price ? `$${Number(product.price).toFixed(2).replace(/\d(?=(\d{3})+\.)/g, '$&,')}` : '-'}
                      </Typography>
                    </Card>
                  </Grid>
                );
              })
              }
            </Grid>
          </Card>
        </form>
      </div>
      <BackdropCircular loading={loadingFullfilmentRequest} title="Sending request..." />
    </div>
  );
}
